import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import produce from 'immer';
import Link from 'next/link';
import {
  faArrowUp,
  faArrowUpFromBracket,
  faComment,
} from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import type { ShareModalSource } from '@soundxyz/utils/analytics';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useModal } from '@context/modal';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  CurrentUserPostVoteStatus,
  FeedPostActionsInfoFragmentDoc,
  PostVoteChange,
  setQueryData,
  useQuery,
  UserVoteOnPostDocument,
} from '@graphql/reactQuery';
import {
  type FragmentType,
  getFragment,
  useMutation,
  VoteOnPostDocument,
} from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';
import { createBatchingInputStore } from '@store/batchInput';
import { webappUriPath } from '@utils/url';
import { SharePostModal } from './SharePostModal';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation VoteOnPost($input: SetVoteOnPostInput!) {
    voteOnPost(input: $input)
  }

  query UserVoteOnPost($postIds: [UUID!]!) {
    userVoteOnPost(input: { postIds: $postIds }) {
      postId
      voteStatus
    }
  }

  fragment FeedPostActionsInfo on Post {
    id
    commentCount
    upvoteCount
    webappUri

    ...SharePostInfo
  }
`);

RefetchOnComplete({
  trigger: [VoteOnPostDocument],
  refetch: [UserVoteOnPostDocument],
});

const { batchedKeys, useBatchedKey } = createBatchingInputStore({
  chunkLimit: 50,
});

export function setVoteStatus({
  postId,
  voteStatus,
}: {
  postId: string;
  voteStatus: CurrentUserPostVoteStatus;
}) {
  const postIds = batchedKeys.keys.get(postId);

  if (!postIds) {
    return;
  }

  setQueryData(
    {
      query: UserVoteOnPostDocument,
      variables: {
        postIds,
      },
    },
    previousData => {
      if (!previousData) return previousData;

      return produce(previousData, draft => {
        for (const value of draft.data.userVoteOnPost) {
          if (value.postId === postId) {
            value.voteStatus = voteStatus;
            return;
          }
        }

        draft.data.userVoteOnPost.push({
          voteStatus,
          postId,
        });
      });
    },
  );
}

type ClientUpvoteCountAction =
  | { type: 'INCREMENT' }
  | { type: 'DECREMENT'; onValueChange?: (value: number) => void }
  | {
      type: 'SET_COUNT';
      payload: { upvoteCount: number };
    }
  | {
      type: 'SET_INITIAL_VOTE_STATUS';
      payload: { initialVoteStatus: CurrentUserPostVoteStatus | undefined };
    };

type ClientUpvoteCountState = {
  clientUpvoteCount: number;
  actualUpvoteCount: number;
  initialVoteStatus: CurrentUserPostVoteStatus | undefined;
};

const clientUpvoteCountReducer = (
  state: ClientUpvoteCountState,
  action: ClientUpvoteCountAction,
): ClientUpvoteCountState => {
  let newUpvoteCount = state.clientUpvoteCount;
  let actualUpvoteCount = state.actualUpvoteCount;
  let initialVoteStatus = state.initialVoteStatus;
  switch (action.type) {
    case 'INCREMENT': {
      if (initialVoteStatus === CurrentUserPostVoteStatus.NoVote) {
        newUpvoteCount = actualUpvoteCount + 1;
      } else {
        newUpvoteCount = actualUpvoteCount;
      }
      break;
    }
    case 'DECREMENT': {
      if (initialVoteStatus === CurrentUserPostVoteStatus.Upvoted) {
        newUpvoteCount = actualUpvoteCount - 1;
      } else {
        newUpvoteCount = actualUpvoteCount;
      }
      break;
    }
    case 'SET_COUNT': {
      actualUpvoteCount = action.payload.upvoteCount;
      newUpvoteCount = action.payload.upvoteCount;
      break;
    }
    case 'SET_INITIAL_VOTE_STATUS': {
      if (initialVoteStatus === undefined) {
        initialVoteStatus = action.payload.initialVoteStatus;
      }

      break;
    }
  }
  return {
    clientUpvoteCount: Math.max(newUpvoteCount, 0),
    actualUpvoteCount,
    initialVoteStatus,
  };
};

export function PostActionButtons({
  releasePostFrag,
  commentsAsLink,

  sourceLocation,
}: {
  releasePostFrag: FragmentType<FeedPostActionsInfoFragmentDoc>;
  commentsAsLink: boolean;

  sourceLocation: ShareModalSource;
}) {
  const releasePost = getFragment(FeedPostActionsInfoFragmentDoc, releasePostFrag);
  const [{ clientUpvoteCount }, clientUpvoteCountDispatch] = useReducer(clientUpvoteCountReducer, {
    clientUpvoteCount: releasePost.upvoteCount,
    actualUpvoteCount: releasePost.upvoteCount,
    initialVoteStatus: undefined,
  });

  const postIds = useBatchedKey({ key: releasePost.id });
  const { authToken } = useAuth(); // prefer authtoken so I don't have to wait a render
  const [isLoading, setIsLoading] = useState(true);
  const { login } = useConnectedAuth();

  const { data: voteStatus } = useQuery(UserVoteOnPostDocument, {
    variables: !!postIds && {
      postIds,
    },
    cacheTime: Infinity,
    select({ data }) {
      for (const statusData of data.userVoteOnPost) {
        if (statusData.postId === releasePost.id) return statusData.voteStatus;
      }

      return undefined;
    },
    enabled: Boolean(authToken) && !!postIds?.length,
    keepPreviousData: true,
    staleTime: 0,
  });

  /**
   * We start off as loading for everyone. If you are logged in,
   * eventually voteStatus will either be "Voted" or "NoVote".
   *
   * If you are logged out, we automatically set isLoading to false.
   * once the component is rendered on the client
   */
  useEffect(() => {
    if (voteStatus !== undefined) {
      setIsLoading(false);
    }
  }, [voteStatus]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (!authToken) {
        setIsLoading(false);
      }
    }
  }, [authToken]);

  const { mutateAsync, isLoading: isVoting } = useMutation(VoteOnPostDocument, {});

  const handleUpvoteClicked = useCallback(async () => {
    if (!authToken) {
      login();
      return;
    }

    if (isVoting) return;

    if (voteStatus === CurrentUserPostVoteStatus.NoVote) {
      setVoteStatus({ postId: releasePost.id, voteStatus: CurrentUserPostVoteStatus.Upvoted });
      clientUpvoteCountDispatch({ type: 'INCREMENT' });
      mutateAsync({ input: { postId: releasePost.id, voteStatus: PostVoteChange.Upvote } });
    } else if (voteStatus === CurrentUserPostVoteStatus.Upvoted) {
      setVoteStatus({ postId: releasePost.id, voteStatus: CurrentUserPostVoteStatus.NoVote });
      clientUpvoteCountDispatch({ type: 'DECREMENT' });
      mutateAsync({ input: { postId: releasePost.id, voteStatus: PostVoteChange.RemoveVote } });
    }
  }, [authToken, voteStatus, login, isVoting, releasePost.id, mutateAsync]);

  const commentsComponent = useMemo(() => {
    return (
      <div className="flex items-center gap-1">
        <FontAwesomeIcon icon={faComment} size="lg" />
        <div>{releasePost.commentCount}</div>
      </div>
    );
  }, [releasePost.commentCount]);

  /**
   * If we get a new upvote count, set it in the client
   */
  useEffect(() => {
    clientUpvoteCountDispatch({
      type: 'SET_COUNT',
      payload: { upvoteCount: releasePost.upvoteCount },
    });
  }, [releasePost.upvoteCount]);

  /**
   * Set vote status to calculate the client count
   */
  useEffect(() => {
    clientUpvoteCountDispatch({
      type: 'SET_INITIAL_VOTE_STATUS',
      payload: { initialVoteStatus: voteStatus },
    });
  }, [voteStatus]);

  const { openModal } = useModal();

  return isLoading ? (
    <div className="-my-1 h-7 w-36 animate-pulse rounded-md bg-neutral200" />
  ) : (
    <div
      className="-mx-2 -my-1 flex h-7 items-center gap-3 font-base text-base-m font-medium text-neutral500"
      data-feed-clickable-div
    >
      {/**
       * Upvote button
       */}
      <button
        className={`flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 ${
          voteStatus === CurrentUserPostVoteStatus.Upvoted ? `text-fuchsia500` : ``
        } hover:bg-neutral100 active:scale-95`}
        onClick={handleUpvoteClicked}
        disabled={isVoting}
      >
        <FontAwesomeIcon icon={faArrowUp} size="lg" />
        <div>{Math.max(clientUpvoteCount, voteStatus === 'UPVOTED' ? 1 : 0)}</div>
      </button>

      {/**
       * Comment button
       */}
      <div className="cursor-pointer rounded-md px-2 py-1 hover:bg-neutral100 active:scale-95">
        {commentsAsLink ? (
          <Link href={webappUriPath(releasePost.webappUri)}>{commentsComponent}</Link>
        ) : (
          commentsComponent
        )}
      </div>

      <div
        className="cursor-pointer rounded-md px-2 py-1 hover:bg-neutral100 active:scale-95"
        onClick={() => {
          trackClickEvent({
            event: 'Share Modal Opened',
            properties: {
              associationType: 'Post',
              associationId: releasePost.id,
              sourceComponent: sourceLocation,
            },
          });
          openModal('SHARED_FEED_POST', {
            body: <SharePostModal post={releasePost} sourceLocation={sourceLocation} />,
          });
        }}
      >
        <FontAwesomeIcon icon={faArrowUpFromBracket} size="lg" />
      </div>
    </div>
  );
}
